import React, { useState, useEffect } from 'react';
import { ArrowLeft, Filter, ChevronDown, ChevronUp, X, Play, Loader2, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTradingTrades, useTradingAnalysis, useQuickReviewTrade } from '../hooks/useTradingAnalysis';
import type { TradingTrade, TradingAnalysis, QuickReviewResult } from '../types/trading';
import { tradingApi } from '../services/tradingApi';
import SignalCard from '../components/trading/SignalCard';
import ConfluenceDetail from '../components/trading/ConfluenceDetail';

const formatDate = (ts: string | null) => {
  if (!ts) return '—';
  let normalized = ts.replace(/^(\d{4})\.(\d{2})\.(\d{2})/, '$1-$2-$3');
  if (!/[Z+-]\d{0,4}$/.test(normalized)) normalized += 'Z';
  const date = new Date(normalized);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const pnlColor = (pnl: number | null) => {
  if (pnl === null || pnl === undefined) return 'text-slate-400';
  if (pnl > 0) return 'text-emerald-400';
  if (pnl < 0) return 'text-red-400';
  return 'text-slate-400';
};

const rowBg = (pnl: number | null) => {
  if (pnl === null || pnl === undefined) return '';
  if (pnl > 1) return 'bg-emerald-500/5';
  if (pnl < -1) return 'bg-red-500/5';
  return 'bg-amber-500/5';
};

const formatReason = (reason: string | null) => {
  if (!reason) return '—';
  const parts = reason.split('|');
  const code = parts[0];
  const comment = parts[1] || '';
  const labels: Record<string, string> = {
    sl_hit: 'SL Hit', tp_hit: 'TP Hit', stop_out: 'Stop Out',
    manual: 'Manual', ea_closed: 'EA', other: 'Other',
  };
  const label = labels[code] || code;
  return comment ? `${label} — ${comment}` : label;
};

// Quick review result display
const QuickReviewDisplay: React.FC<{ result: QuickReviewResult }> = ({ result }) => {
  const review = result.trade_reviews?.[0];
  const assessmentColor: Record<string, string> = {
    correct: 'text-emerald-400',
    incorrect: 'text-red-400',
    partially_correct: 'text-amber-400',
  };
  const entryColor: Record<string, string> = {
    excellent: 'text-emerald-400',
    good: 'text-teal-400',
    fair: 'text-amber-400',
    poor: 'text-red-400',
  };

  return (
    <div className="bg-slate-900/50 rounded-lg border border-slate-700/30 p-4 space-y-3">
      <h4 className="text-xs font-medium text-teal-400">Quick Review Result</h4>
      {review && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500">Assessment</span>
            <p className={`font-medium ${assessmentColor[review.assessment] || 'text-slate-300'}`}>
              {review.assessment}
            </p>
          </div>
          <div>
            <span className="text-slate-500">Entry Quality</span>
            <p className={`font-medium ${entryColor[review.entry_quality] || 'text-slate-300'}`}>
              {review.entry_quality}
            </p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-500">What Happened</span>
            <p className="text-slate-300">{review.what_happened}</p>
          </div>
          <div className="col-span-2">
            <span className="text-slate-500">Lesson</span>
            <p className="text-slate-300">{review.lesson}</p>
          </div>
        </div>
      )}
      {result.summary && (
        <p className="text-xs text-slate-400 border-t border-slate-700/30 pt-2">{result.summary}</p>
      )}
      {result.insights_generated > 0 && (
        <p className="text-xs text-teal-400">{result.insights_generated} insight(s) generated — check Insights page to review</p>
      )}
    </div>
  );
};

// Expandable row detail showing linked analysis
const TradeDetail: React.FC<{ trade: TradingTrade }> = ({ trade }) => {
  const { data: analysis, isLoading } = useTradingAnalysis(trade.analysis_id || '');
  const quickReview = useQuickReviewTrade();
  const [reviewResult, setReviewResult] = useState<QuickReviewResult | null>(null);
  const [entryScreenshots, setEntryScreenshots] = useState<string[]>([]);
  const [expandedScreenshot, setExpandedScreenshot] = useState<string | null>(null);

  useEffect(() => {
    tradingApi.getTradeEntryScreenshots(trade.ticket).then((res) => {
      setEntryScreenshots(res.timeframes);
    }).catch(() => {});
  }, [trade.ticket]);

  const handleQuickReview = () => {
    quickReview.mutate(trade.ticket, {
      onSuccess: (data) => setReviewResult(data),
    });
  };

  return (
    <div className="px-6 py-4 bg-slate-800/80 border-t border-slate-700/30 space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="text-slate-500">Open Price</span>
          <p className="text-white font-medium">{trade.open_price}</p>
        </div>
        <div>
          <span className="text-slate-500">Close Price</span>
          <p className="text-white font-medium">{trade.close_price ?? '—'}</p>
        </div>
        <div>
          <span className="text-slate-500">SL / TP</span>
          <p className="text-white font-medium">{trade.sl_set ?? '—'} / {trade.tp_set ?? '—'}</p>
        </div>
        <div>
          <span className="text-slate-500">Duration</span>
          <p className="text-white font-medium">{trade.duration_minutes ? `${trade.duration_minutes}m` : '—'}</p>
        </div>
      </div>

      {/* Quick Review Button — only for closed trades */}
      {trade.close_time && (
        <div>
          {reviewResult ? (
            <QuickReviewDisplay result={reviewResult} />
          ) : (
            <button
              onClick={handleQuickReview}
              disabled={quickReview.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-teal-600/20 hover:bg-teal-600/30 text-teal-400 text-sm rounded-lg border border-teal-500/30 transition-colors disabled:opacity-50"
            >
              {quickReview.isPending ? (
                <><Loader2 size={14} className="animate-spin" /> Reviewing...</>
              ) : (
                <><Play size={14} /> Quick Review</>
              )}
            </button>
          )}
          {quickReview.isError && (
            <p className="text-xs text-red-400 mt-1">{(quickReview.error as Error).message}</p>
          )}
        </div>
      )}

      {/* Entry Screenshots */}
      {entryScreenshots.length > 0 && (
        <div>
          <h4 className="text-xs text-slate-400 mb-2 flex items-center gap-1">
            <Camera size={12} /> Entry Charts ({entryScreenshots.join(', ')})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {entryScreenshots.map((tf) => (
              <div key={tf} className="relative">
                <button
                  onClick={() => setExpandedScreenshot(expandedScreenshot === tf ? null : tf)}
                  className="w-full"
                >
                  <img
                    src={tradingApi.getTradeEntryScreenshotUrl(trade.ticket, tf)}
                    alt={`${tf} entry chart`}
                    className="w-full rounded-lg border border-slate-700/50 hover:border-teal-500/50 transition-colors cursor-pointer"
                  />
                  <span className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    {tf}
                  </span>
                </button>
              </div>
            ))}
          </div>
          {expandedScreenshot && (
            <div
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center cursor-pointer"
              onClick={() => setExpandedScreenshot(null)}
            >
              <img
                src={tradingApi.getTradeEntryScreenshotUrl(trade.ticket, expandedScreenshot)}
                alt={`${expandedScreenshot} entry chart`}
                className="max-w-[90vw] max-h-[90vh] rounded-lg"
              />
              <span className="absolute top-4 right-4 text-white bg-black/50 px-3 py-1 rounded-lg text-sm">
                {expandedScreenshot} — click to close
              </span>
            </div>
          )}
        </div>
      )}

      {trade.analysis_id && (
        <div>
          <h4 className="text-xs text-slate-400 mb-2">Linked Analysis</h4>
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading analysis...</div>
          ) : analysis ? (
            <>
              <SignalCard analysis={analysis as TradingAnalysis} />
              {(analysis.key_levels || analysis.fair_value_gaps || analysis.indicators) && (
                <div className="mt-3">
                  <h4 className="text-xs text-slate-400 mb-2">Entry Confluences</h4>
                  <ConfluenceDetail analysis={analysis as TradingAnalysis} />
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-500">Analysis not found</div>
          )}
        </div>
      )}

      {!trade.analysis_id && (
        <div className="text-sm text-slate-500">No linked analysis (trade from external EA)</div>
      )}
    </div>
  );
};

const TradingTradeReview: React.FC = () => {
  const navigate = useNavigate();
  const { data: trades = [], isLoading } = useTradingTrades();
  const [expandedTicket, setExpandedTicket] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [resultFilter, setResultFilter] = useState<'all' | 'win' | 'loss'>('all');
  const [reasonFilter, setReasonFilter] = useState('');

  // Derive unique symbols and reasons
  const symbols = [...new Set(trades.map((t) => t.symbol))];
  const reasons = [...new Set(trades.map((t) => t.close_reason).filter(Boolean))];

  // Apply filters
  const filtered = trades.filter((t) => {
    if (symbolFilter && t.symbol !== symbolFilter) return false;
    if (resultFilter === 'win' && (t.profit_loss ?? 0) <= 0) return false;
    if (resultFilter === 'loss' && (t.profit_loss ?? 0) >= 0) return false;
    if (reasonFilter && t.close_reason !== reasonFilter) return false;
    return true;
  });

  // Summary stats
  const closedTrades = filtered.filter((t) => t.close_time);
  const winners = closedTrades.filter((t) => (t.profit_loss ?? 0) > 0);
  const losers = closedTrades.filter((t) => (t.profit_loss ?? 0) < 0);
  const grossProfit = winners.reduce((s, t) => s + (t.profit_loss ?? 0), 0);
  const grossLoss = losers.reduce((s, t) => s + (t.profit_loss ?? 0), 0);
  const winRate = closedTrades.length ? (winners.length / closedTrades.length) * 100 : 0;
  const profitFactor = grossLoss !== 0 ? Math.abs(grossProfit / grossLoss) : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/trading')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Trade Review</h1>
            <p className="text-slate-400 text-sm">Review trades with linked analyses and entry screenshots</p>
          </div>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-lg border border-slate-700/50"
        >
          <Filter size={14} />
          Filters
          {(symbolFilter || resultFilter !== 'all' || reasonFilter) && (
            <span className="w-2 h-2 bg-teal-400 rounded-full" />
          )}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Symbol</label>
            <select
              value={symbolFilter}
              onChange={(e) => setSymbolFilter(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="">All</option>
              {symbols.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Result</label>
            <select
              value={resultFilter}
              onChange={(e) => setResultFilter(e.target.value as 'all' | 'win' | 'loss')}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="all">All</option>
              <option value="win">Winners</option>
              <option value="loss">Losers</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Close Reason</label>
            <select
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white"
            >
              <option value="">All</option>
              {reasons.map((r) => <option key={r} value={r!}>{formatReason(r!)}</option>)}
            </select>
          </div>
          <button
            onClick={() => { setSymbolFilter(''); setResultFilter('all'); setReasonFilter(''); }}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1.5"
          >
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: closedTrades.length, color: 'text-white' },
          { label: 'Win Rate', value: `${winRate.toFixed(1)}%`, color: winRate >= 50 ? 'text-emerald-400' : 'text-red-400' },
          { label: 'Profit Factor', value: profitFactor.toFixed(2), color: profitFactor >= 1.5 ? 'text-emerald-400' : 'text-amber-400' },
          { label: 'Avg Win', value: winners.length ? `$${(grossProfit / winners.length).toFixed(2)}` : '—', color: 'text-emerald-400' },
          { label: 'Avg Loss', value: losers.length ? `$${(grossLoss / losers.length).toFixed(2)}` : '—', color: 'text-red-400' },
        ].map((s) => (
          <div key={s.label} className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-3">
            <span className="text-xs text-slate-400">{s.label}</span>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Trade Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No trades match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-700/50">
                  <th className="px-4 py-3 font-medium w-8"></th>
                  <th className="px-4 py-3 font-medium">Ticket</th>
                  <th className="px-4 py-3 font-medium">Symbol</th>
                  <th className="px-4 py-3 font-medium">Direction</th>
                  <th className="px-4 py-3 font-medium">Open</th>
                  <th className="px-4 py-3 font-medium">Close</th>
                  <th className="px-4 py-3 font-medium text-right">Lot</th>
                  <th className="px-4 py-3 font-medium text-right">P/L</th>
                  <th className="px-4 py-3 font-medium">Reason</th>
                  <th className="px-4 py-3 font-medium text-right">Magic</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((trade) => (
                  <React.Fragment key={trade.ticket}>
                    <tr
                      onClick={() => setExpandedTicket(expandedTicket === trade.ticket ? null : trade.ticket)}
                      className={`border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition-colors ${rowBg(trade.profit_loss)}`}
                    >
                      <td className="px-4 py-3 text-slate-500">
                        {expandedTicket === trade.ticket ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </td>
                      <td className="px-4 py-3 text-slate-300">#{trade.ticket}</td>
                      <td className="px-4 py-3 font-medium text-white">{trade.symbol}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          trade.direction === 'BUY' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                          {trade.direction}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{formatDate(trade.open_time)}</td>
                      <td className="px-4 py-3 text-slate-400">{formatDate(trade.close_time)}</td>
                      <td className="px-4 py-3 text-right text-slate-300">{trade.lot_size}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${pnlColor(trade.profit_loss)}`}>
                        {trade.profit_loss !== null && trade.profit_loss !== undefined ? `$${trade.profit_loss.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{formatReason(trade.close_reason)}</td>
                      <td className="px-4 py-3 text-right text-slate-500 text-xs">{trade.magic_number || '—'}</td>
                    </tr>
                    {expandedTicket === trade.ticket && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          <TradeDetail trade={trade} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingTradeReview;
